import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { apiClient } from 'Helpers/network';

import useApiRequest from 'Hooks/useApiRequest';
import { useToast } from 'Hooks/useToast';

const DEFAULT_CRITERIA = {
    budget_id: null,
    scope_ids: [],
    report_type: 'summary',
    // Filled in from the criteria panel, which pre-fills it from the rest of
    // these values. Stored on the run so the archive can name it.
    name: '',
    // Off by default: the Detailed Description toggle is disabled for the
    // summary report type, and a disabled toggle must read as switched off.
    include_detailed_description: false,
    include_workforce: false,
    include_cash: false,
};

/**
 * The criteria the preview endpoints hash to pick a cache slot.
 *
 * The cached GET and the refresh POST have to land on the same slot, so the
 * flags go over the wire as 1/0: a JSON `false` reaches PHP as a boolean, but
 * the same value on a query string arrives as the string "false", which casts
 * to true. "0" and 0 both cast to false.
 *
 * `name` rides along without being hashed — the server hashes the criteria that
 * change the payload, and it only stores the name on the row it creates.
 */
const previewCriteriaPayload = (criteria) => ({
    budget_id: criteria?.budget_id ?? null,
    scope_ids: criteria?.scope_ids ?? [],
    report_type: criteria?.report_type ?? DEFAULT_CRITERIA.report_type,
    include_workforce: criteria?.include_workforce ? 1 : 0,
    include_detailed_description: criteria?.include_detailed_description ? 1 : 0,
    include_cash: criteria?.include_cash ? 1 : 0,
    name: criteria?.name || null,
});

/**
 * Identifies the cache slot a set of criteria addresses.
 *
 * Everything the server hashes and nothing else, so two criteria that differ
 * only by `name` produce the same key: the name is stored on the run the server
 * records, not hashed into the slot, and treating a rename as a criteria change
 * would clear a rendered report to fetch the same one back.
 *
 * Derived from the request payload rather than listed again, so a new criteria
 * field is part of the key as soon as it is sent.
 */
export const previewCriteriaCacheKey = (criteria) => {
    const { name, ...hashed } = previewCriteriaPayload(criteria);

    // Ordering is not part of the slot — the server sorts the ids before
    // hashing them — so neither should it be part of the key.
    return JSON.stringify({ ...hashed, scope_ids: [...hashed.scope_ids].map(Number).sort((a, b) => a - b) });
};

const BudgetByDimensionReportContext = createContext();

export const useBudgetByDimensionReportContext = () => {
    const context = useContext(BudgetByDimensionReportContext);

    if (!context) {
        throw new Error('useBudgetByDimensionReportContext must be used within a BudgetByDimensionReportContext');
    }

    return context;
};

export const BudgetByDimensionReportProvider = ({
    generateReportUrl,
    fetchReportUrl,
    previewCachedUrl,
    previewRefreshUrl,
    previewShowUrl,
    emailReportUrl,
    fetchArchivedReportsUrl,
    deleteArchivedReportUrl,
    downloadArchivedReportUrl,
    checkStatusUrl,
    showBudgetReviewUrl,
    editBudgetReviewUrl,
    deleteBudgetReviewUrl,
    deleteBudgetReviewActionUrl,
    budgetVersions,
    scopeOptionsByBudget,
    currentBudgetId,
    isVikEnabled,
    isWorkforceModuleEnabled,
    channelName,
    dimension,
    pruningDays,
    children,
}) => {
    const [criteria, setCriteria] = useState({
        ...DEFAULT_CRITERIA,
    });

    // Bumped on every submit, including one that changed nothing. The preview
    // reads this rather than the criteria themselves: the panel opens pre-filled
    // with these defaults, so a first Generate submits values identical to them
    // and would otherwise register as no request at all.
    const [criteriaVersion, setCriteriaVersion] = useState(0);

    const fetchArchivedReportsRequest = useApiRequest({ url: fetchArchivedReportsUrl });
    const deleteArchivedReportRequest = useApiRequest({ url: deleteArchivedReportUrl });

    const toast = useToast();

    const configs = useMemo(
        () => ({
            budgetVersions: budgetVersions || [],
            scopeOptionsByBudget: scopeOptionsByBudget || {},
            currentBudgetId,
        }),
        [budgetVersions, scopeOptionsByBudget, currentBudgetId],
    );

    const fetchArchivedReportsData = useCallback(
        (pagination) => {
            const { reset, get } = fetchArchivedReportsRequest;

            reset();

            return get({
                page: pagination.page,
                per_page: pagination.perPage,
            });
        },
        [fetchArchivedReportsRequest.reset, fetchArchivedReportsRequest.get],
    );

    const deleteArchivedReport = useCallback(
        (reportId) => {
            const { delete: deleteRequest } = deleteArchivedReportRequest;

            return deleteRequest(null, { REPORT_CENTRE_EXPORT_ID: reportId });
        },
        [deleteArchivedReportRequest.delete],
    );

    const downloadArchivedReport = useCallback(
        (reportId) => {
            const url = downloadArchivedReportUrl.replace('REPORT_CENTRE_EXPORT_ID', reportId);
            window.open(url, '_blank');
        },
        [downloadArchivedReportUrl],
    );

    const deleteBudgetReview = useCallback(
        (reviewId) => {
            return apiClient
                .delete(deleteBudgetReviewUrl.replace('BUDGETS_REVIEW_ID', reviewId))
                .then(() => {
                    toast.success($t('Budget review deleted successfully.'));
                })
                .catch((e) => {
                    toast.error($t('Failed to delete the budget review. Please try again.'));
                    throw e;
                });
        },
        [deleteBudgetReviewUrl, toast],
    );

    const deleteBudgetReviewAction = useCallback(
        (reviewId, actionId) => {
            const url = deleteBudgetReviewActionUrl.replace('BUDGETS_REVIEW_ID', reviewId).replace('BUDGETS_REVIEW_ACTION_ID', actionId);

            return apiClient
                .delete(url)
                .then(() => {
                    toast.success($t('Review action deleted successfully.'));
                })
                .catch((e) => {
                    toast.error($t('Failed to delete the review action. Please try again.'));
                    throw e;
                });
        },
        [deleteBudgetReviewActionUrl, toast],
    );

    const generateReport = useCallback(() => {
        return apiClient.post(generateReportUrl, criteria).then(() => {
            toast.success($t('The request to generate a report is being processed. This may take a few minutes'));
        });
    }, [generateReportUrl, criteria, toast]);

    const fetchReport = useCallback(() => {
        if (!fetchReportUrl) {
            return Promise.resolve(null);
        }
        return apiClient.get(fetchReportUrl, { params: criteria });
    }, [fetchReportUrl, criteria]);

    /**
     * Ask what the background preview holds for these criteria. Always answers
     * 200 with one of cached / generating / failed / not_found.
     */
    const fetchPreviewCached = useCallback(
        (previewCriteria) => {
            if (!previewCachedUrl) {
                return Promise.reject(new Error('Preview cached URL not configured'));
            }

            return apiClient.get(previewCachedUrl, { params: previewCriteriaPayload(previewCriteria) }).then((response) => response.data);
        },
        [previewCachedUrl],
    );

    /**
     * Queue a background generation for these criteria. A run already under way
     * on the same criteria is joined rather than duplicated.
     */
    const refreshPreview = useCallback(
        (previewCriteria) => {
            if (!previewRefreshUrl) {
                return Promise.reject(new Error('Preview refresh URL not configured'));
            }

            return apiClient.post(previewRefreshUrl, previewCriteriaPayload(previewCriteria)).then((response) => response.data);
        },
        [previewRefreshUrl],
    );

    /**
     * Read one archived preview by its row, for the archive's View action. The
     * artifact is read from the path stored on the row, so this answers with
     * that run's own preview rather than whatever the caller's criteria hold.
     */
    const fetchPreviewReport = useCallback(
        (reportId) => {
            if (!previewShowUrl) {
                return Promise.reject(new Error('Preview show URL not configured'));
            }

            return apiClient.get(previewShowUrl.replace('REPORT_CENTRE_EXPORT_ID', reportId)).then((response) => response.data);
        },
        [previewShowUrl],
    );

    const sendReportEmail = useCallback(
        (email) => {
            if (!emailReportUrl) {
                return Promise.reject(new Error('Email report URL not configured'));
            }
            return apiClient.post(emailReportUrl, { ...criteria, email });
        },
        [emailReportUrl, criteria],
    );

    const updateCriteria = useCallback((newCriteria) => {
        setCriteria((prevCriteria) => ({
            ...prevCriteria,
            ...newCriteria,
        }));

        setCriteriaVersion((version) => version + 1);
    }, []);

    const value = {
        criteria,
        criteriaVersion,
        configs,
        isVikEnabled,
        isWorkforceModuleEnabled,
        dimension,
        channelName,
        pruningDays,
        loadingArchivedReports: fetchArchivedReportsRequest.loading,
        archivedReportsData: fetchArchivedReportsRequest.data,
        showBudgetReviewUrl,
        editBudgetReviewUrl,
        updateCriteria,
        generateReport,
        fetchReport,
        fetchPreviewCached,
        refreshPreview,
        fetchPreviewReport,
        sendReportEmail,
        fetchArchivedReportsData,
        deleteArchivedReport,
        downloadArchivedReport,
        deleteBudgetReview,
        deleteBudgetReviewAction,
    };

    return <BudgetByDimensionReportContext.Provider value={value}>{children}</BudgetByDimensionReportContext.Provider>;
};

export default BudgetByDimensionReportContext;
